import {
  GetGoogleImageResponse,
  GetGooglePlaceAutocompleteResponse,
  GetGooglePlaceDetailResponse,
  GetGooglePlaceImagesResponse,
  GetGooglePlaceRatingResponse,
  TripGooglePlaceDetail,
} from '../places/types'
import { baseUri, getOptions } from '../utils'

import request from 'api/requestUtils'

// This function is used to fetch a single image for a place when we already know the name of the resource
export async function getGooglePlacePhoto(
  name: string,
  accessToken?: string,
): Promise<GetGoogleImageResponse['result']> {
  const uri = `${baseUri}/places/photos`
  const params = new URLSearchParams([])
  params.append('name', name)
  const encodedUri = uri + '?' + params.toString()
  return request
    .get<GetGoogleImageResponse>(encodedUri, {
      ...getOptions(accessToken),
    })
    .then((response) => response.result)
}

// This function is used to fetch multiple images for a place when we only know the placeId
export async function getGooglePlaceImages(
  placeId: string,
  count: number = 1,
  orientation: 'landscape' | 'portrait' | 'all' = 'all',
  maxWidthPx?: number,
): Promise<GetGooglePlaceImagesResponse['result']> {
  const uri = `${baseUri}/places/${placeId}/photos`
  const params = new URLSearchParams([])
  params.append('count', count.toString())
  params.append('orientation', orientation)
  if (maxWidthPx) {
    params.append('maxWidthPx', maxWidthPx.toString())
  }
  const encodedUri = uri + '?' + params.toString()
  return request
    .get<GetGooglePlaceImagesResponse>(encodedUri, {
      ...getOptions(),
    })
    .then((response) => response.result)
}

export async function getGooglePlaceRating(placeId: string) {
  const uri = `${baseUri}/places/${placeId}/rating`
  return request
    .get<GetGooglePlaceRatingResponse>(uri, {
      ...getOptions(),
    })
    .then((response) => response.result)
}

export interface GooglePlaceAutocompleteRequest {
  input: string
  types?: string[]
  sessionToken?: string
  locationBias?: { lat: number; lng: number }
  radius?: number
  origin?: { lat: number; lng: number }
  components?: string[]
}

export async function getGooglePlaceAutocomplete({
  input,
  types,
  sessionToken,
  locationBias,
  radius,
  origin,
  components,
}: GooglePlaceAutocompleteRequest): Promise<
  GetGooglePlaceAutocompleteResponse['result']
> {
  const uri = `${baseUri}/places/autocomplete`
  const params = new URLSearchParams([])
  params.append('input', input)
  if (types) {
    types.forEach((type) => {
      params.append('types[]', type)
    })
  }
  if (sessionToken) {
    params.append('sessiontoken', sessionToken)
  }
  if (origin) {
    params.append('origin', `${origin.lat},${origin.lng}`)
  }
  if (locationBias) {
    params.append('locationBias', `${locationBias.lat},${locationBias.lng}`)
  }
  if (radius) {
    params.append('radius', radius.toString())
  }
  if (components) {
    components.forEach((component) => {
      params.append('components[]', component)
    })
  }
  const encodedUri = uri + '?' + params.toString()
  return request
    .get<GetGooglePlaceAutocompleteResponse>(encodedUri, {
      ...getOptions(),
    })
    .then((response) => response.result)
}

export interface GooglePlaceDetailRequest<
  Field extends keyof TripGooglePlaceDetail,
> {
  placeId: string
  fields?: Field[]
}
export async function getGooglePlaceDetail({
  placeId,
  fields,
}: GooglePlaceDetailRequest<
  keyof TripGooglePlaceDetail
>): Promise<TripGooglePlaceDetail> {
  const uri = `${baseUri}/places/${placeId}/detail`
  const params = new URLSearchParams([])
  if (fields) {
    fields.forEach((field) => {
      params.append('fields[]', field)
    })
  }
  const encodedUri = uri + '?' + params.toString()
  return request
    .get<GetGooglePlaceDetailResponse>(encodedUri, {
      ...getOptions(),
    })
    .then((response) => response.result)
}
